import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Clock, Loader2, Mail, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { isPurchasable } from "@/data/books";
import { track } from "@/lib/analytics";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../ui/dialog";
import { Input } from "../ui/input";

const API = process.env.REACT_APP_BACKEND_URL;
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

let razorpayScriptPromise = null;
export function loadRazorpayCheckout() {
  if (window.Razorpay) return Promise.resolve();
  if (!razorpayScriptPromise) {
    razorpayScriptPromise = new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.onload = () => resolve();
      script.onerror = () => reject(new Error("razorpay_script_failed"));
      document.body.appendChild(script);
    });
  }
  return razorpayScriptPromise;
}

/**
 * BuyButton — single source of the purchase CTA across the Chinnu store.
 *
 * Flow (Standard Checkout + Orders API, server-authoritative price):
 *  0. Buy Now opens a lightweight email-capture prompt (optional — buyer
 *     can skip). The email is stored server-side against the checkout so
 *     the post-purchase delivery email is reliable even if Razorpay's own
 *     checkout UI doesn't surface one.
 *  1. POST /api/store/checkout/create { product_id, email } → { checkout_id, order_id, amount, currency, key_id }
 *  2. Open Razorpay Standard Checkout with that order_id/amount/key_id.
 *  3. On success, POST /api/store/checkout/verify with the returned
 *     razorpay_payment_id/order_id/signature — never trusted client-side.
 *  4. Only on verified:true do we navigate to the Thank You page, and only
 *     with the signed download token the backend issued — the frontend
 *     never decides access on its own.
 * If checkout/create is unavailable (Razorpay not configured yet, or a
 * network/API error), we fall back to the product's Razorpay Payment Link
 * if one exists (Chinnu's owner-supplied backup links); products with no
 * payment link (e.g. Cyber Safety, which relies solely on this Standard
 * Checkout engine) show a friendly retry toast instead.
 */
export default function BuyButton({
  product,
  label,
  pendingLabel = "Payment link pending",
  source = "unknown",
  testId,
  dark = false,
  className = "",
  accentColor = "#F57F17",
}) {
  const [loading, setLoading] = useState(false);
  const [showEmailPrompt, setShowEmailPrompt] = useState(false);
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const navigate = useNavigate();
  const canBuy = isPurchasable(product);
  const finalLabel = label || `Buy Now — ${product.price}`;
  const orderBase = product.order_base || "/books/chinnu/order";

  const trackPayload = () => ({
    product_id: product.product_id,
    offer_price: product.price_value,
    regular_price: product.regular_price_value,
    source,
  });

  const fallbackToPaymentLink = () => {
    if (product.razorpay_payment_link) {
      track("razorpay_outbound_click", trackPayload());
      window.location.assign(product.razorpay_payment_link);
      return;
    }
    toast.error("Checkout is temporarily unavailable. Please try again in a moment.");
  };

  const startCheckout = async (buyerEmail) => {
    setShowEmailPrompt(false);
    track("buy_digital_click", trackPayload());
    setLoading(true);
    try {
      await loadRazorpayCheckout();
      const createRes = await fetch(`${API}/api/store/checkout/create`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ product_id: product.product_id, email: buyerEmail || undefined }),
      });
      if (!createRes.ok) {
        setLoading(false);
        fallbackToPaymentLink();
        return;
      }
      const order = await createRes.json();

      const rzp = new window.Razorpay({
        key: order.key_id,
        order_id: order.order_id,
        amount: order.amount,
        currency: order.currency,
        name: order.name,
        description: order.description,
        prefill: buyerEmail ? { email: buyerEmail } : undefined,
        theme: { color: accentColor },
        modal: {
          ondismiss: () => setLoading(false),
        },
        handler: async (response) => {
          try {
            const verifyRes = await fetch(`${API}/api/store/checkout/verify`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                checkout_id: order.checkout_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_order_id: response.razorpay_order_id,
                razorpay_signature: response.razorpay_signature,
              }),
            });
            const verify = await verifyRes.json();
            track("checkout_verify_result", { product_id: product.product_id, verified: Boolean(verify.verified) });
            if (verify.verified && verify.token) {
              navigate(`${orderBase}/${verify.slug}?token=${encodeURIComponent(verify.token)}&ref=${encodeURIComponent(response.razorpay_payment_id)}`);
            } else {
              navigate(`${orderBase}/${product.download_slug || product.slug}?ref=${encodeURIComponent(response.razorpay_payment_id)}`);
            }
          } finally {
            setLoading(false);
          }
        },
      });
      rzp.open();
    } catch (err) {
      setLoading(false);
      fallbackToPaymentLink();
    }
  };

  const onBuyClick = () => {
    if (!canBuy || loading) return;
    setEmailError("");
    setShowEmailPrompt(true);
  };

  const onContinueWithEmail = () => {
    const trimmed = email.trim();
    if (trimmed && !EMAIL_RE.test(trimmed)) {
      setEmailError("That doesn't look like a valid email — please check it, or skip.");
      return;
    }
    startCheckout(trimmed || null);
  };

  return (
    <span className={`inline-flex flex-col ${className}`}>
      <button
        type="button"
        data-testid={testId}
        onClick={onBuyClick}
        disabled={!canBuy || loading}
        style={canBuy ? { backgroundColor: accentColor } : undefined}
        className={`inline-flex items-center justify-center gap-2 px-7 py-4 text-[13px] font-semibold tracking-wide uppercase rounded-sm transition-colors duration-200 ${
          canBuy
            ? "text-white hover:opacity-90 disabled:opacity-80"
            : dark
              ? "bg-white/10 text-white/55 cursor-not-allowed"
              : "bg-[#E5E7EA] text-[#5C6470] cursor-not-allowed"
        }`}
      >
        {loading ? <Loader2 size={15} className="animate-spin" /> : canBuy ? <ArrowRight size={15} /> : <Clock size={14} />}
        {canBuy ? (loading ? "Opening secure checkout…" : finalLabel) : pendingLabel}
      </button>
      {canBuy && (
        <span className={`mt-2.5 inline-flex items-center justify-center gap-1.5 text-[11px] uppercase tracking-[0.14em] font-semibold ${dark ? "text-white/55" : "text-[#5C6470]"}`}>
          <ShieldCheck size={12} style={{ color: accentColor }} /> Secure payment through Razorpay
        </span>
      )}

      <Dialog open={showEmailPrompt} onOpenChange={setShowEmailPrompt}>
        <DialogContent
          data-testid="buy-email-capture-dialog"
          className="bg-[#FBF7EE] border-[#E5E1D3] max-w-md rounded-sm"
        >
          <DialogHeader>
            <DialogTitle className="text-[#0D1B2A] font-serif text-xl flex items-center gap-2">
              <Mail size={18} style={{ color: accentColor }} /> Get your download link by email too
            </DialogTitle>
            <DialogDescription className="text-[#5C6470] text-sm pt-1">
              You'll be able to download {product.title || "your book"} instantly after payment — we'll also email your
              secure download link as a backup, in case you need it later.
            </DialogDescription>
          </DialogHeader>

          <Input
            type="email"
            data-testid="buy-email-input"
            placeholder="you@example.com"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              setEmailError("");
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") onContinueWithEmail();
            }}
            className="border-[#D9D4C4] text-[#0D1B2A]"
            style={{ "--tw-ring-color": accentColor }}
            autoFocus
          />
          {emailError && (
            <p data-testid="buy-email-error" className="text-xs text-red-600 -mt-2">
              {emailError}
            </p>
          )}

          <div className="flex flex-col gap-2.5 pt-1">
            <button
              type="button"
              data-testid="buy-email-continue-btn"
              onClick={onContinueWithEmail}
              style={{ backgroundColor: accentColor }}
              className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 text-[13px] font-semibold tracking-wide uppercase rounded-sm text-white hover:opacity-90 transition-opacity"
            >
              Continue to Payment <ArrowRight size={14} />
            </button>
            <button
              type="button"
              data-testid="buy-email-skip-btn"
              onClick={() => startCheckout(null)}
              className="text-[12px] font-medium uppercase tracking-wide text-[#5C6470] hover:text-[#0D1B2A] transition-colors py-1"
            >
              Skip and pay without email
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </span>
  );
}
